// One chip per report_schedule for the clinic in view. Owner-only pause
// switch — pausing does not touch Meevo; intake still files.
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { authedGet, authedWrite } from '../lib/apiClient';
import {
  formatLastReceived,
  groupSchedulesByCode,
  presenceTitle,
  reportSchedulesKey,
  scheduleStateLabel,
  type ReportSchedule,
  type ReportScheduleList,
} from '../lib/reportSchedules';
import type { LocationCode } from '../lib/locations';
import { session } from '../lib/session';
import { refetchResource, useResource } from '../lib/store';
import { EmptyState, FaultState } from './FaultState';

const PAUSE_NOTE = 'Paused reports still arrive and still file. This only stops the app expecting them.';

function loadSchedules(clinic: string): Promise<ReportScheduleList> {
  const q = new URLSearchParams({ location: clinic });
  return authedGet<ReportScheduleList>(`/api/v1/report-schedules?${q.toString()}`);
}

function canToggleSchedule(
  me: { role?: string | null; viewingAs?: boolean } | null | undefined = session.value.me,
): boolean {
  if (!me || me.viewingAs) return false;
  return me.role === 'OWNER';
}

function PresenceStrip({ days }: { days: ReportSchedule['presence'] }): JSX.Element {
  const strip = days.length ? days : [];
  return (
    <div class="report-presence" data-testid="report-presence-strip" aria-label="Last 30 clinic days">
      {strip.map((day) => (
        <span
          key={day.date}
          class={day.received ? 'report-presence-day is-filled' : 'report-presence-day'}
          title={presenceTitle(day)}
          data-testid="report-presence-day"
          data-date={day.date}
          data-received={day.received ? 'true' : 'false'}
        />
      ))}
    </div>
  );
}

function ScheduleChip({
  row,
  clinic,
  expanded,
  onToggleOpen,
  canToggle,
}: {
  row: ReportSchedule;
  clinic: string;
  expanded: boolean;
  onToggleOpen: () => void;
  canToggle: boolean;
}): JSX.Element {
  const [busy, setBusy] = useState(false);

  const onSwitch = async (ev: Event): Promise<void> => {
    ev.stopPropagation();
    if (!canToggle || busy) return;
    const active = (ev.currentTarget as HTMLInputElement).checked;
    setBusy(true);
    try {
      await authedWrite('PATCH', `/api/v1/report-schedules/${row.id}`, { active });
      await refetchResource(reportSchedulesKey(clinic), () => loadSchedules(clinic));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      class={row.active ? 'report-chip' : 'report-chip is-paused'}
      data-testid={`report-chip-${row.id}`}
      data-state={row.state}
    >
      <div class="report-chip-row">
        <button
          type="button"
          class="report-chip-main"
          aria-expanded={expanded}
          onClick={onToggleOpen}
        >
          <span
            class="report-chip-dot"
            data-state={row.state}
            data-testid="report-chip-state"
            title={scheduleStateLabel(row.state)}
            aria-label={scheduleStateLabel(row.state)}
          />
          <span class="report-chip-label">{row.label}</span>
          <span class="report-chip-last">
            last received {formatLastReceived(row.lastReceivedAt)}
          </span>
          {row.neverArrived ? (
            <span class="report-chip-never" data-testid="report-never-arrived">
              Never arrived
            </span>
          ) : null}
        </button>
        {canToggle ? (
          <label class="report-chip-switch" onClick={(ev) => ev.stopPropagation()}>
            <input
              type="checkbox"
              role="switch"
              checked={row.active}
              disabled={busy}
              aria-label={row.active ? `Expect ${row.label}` : `Paused ${row.label}`}
              data-testid="report-chip-switch"
              onChange={onSwitch}
            />
          </label>
        ) : null}
      </div>
      {expanded ? (
        <div class="report-chip-panel">
          <PresenceStrip days={row.presence} />
          <p class="report-chip-counts">
            7 days {row.received7} of {row.expected7} expected
            {' · '}
            30 days {row.received30} of {row.expected30} expected
          </p>
          <p class="report-chip-links">
            {row.lastFileUrl ? (
              <a href={row.lastFileUrl} target="_blank" rel="noreferrer">Last file</a>
            ) : (
              <span>No file yet</span>
            )}
            {row.folderUrl ? (
              <>
                {' · '}
                <a href={row.folderUrl} target="_blank" rel="noreferrer">Folder</a>
              </>
            ) : null}
          </p>
          {row.neverArrived ? (
            <p class="report-chip-never-note">This report has never arrived.</p>
          ) : (
            <p class="report-chip-gap" data-testid="report-gap-label">
              A hollow day is not received.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function ReportScheduleBoard({ clinic }: { clinic: LocationCode }): JSX.Element {
  const key = reportSchedulesKey(clinic);
  const load = useResource<ReportScheduleList>(key, () => loadSchedules(clinic));
  const [openId, setOpenId] = useState<number | null>(null);
  const canToggle = canToggleSchedule();

  if (load.status === 'loading') {
    return <EmptyState label="Loading report schedules…" />;
  }
  if (load.status === 'fault') {
    return <FaultState name="report schedules" detail={load.error} />;
  }

  const schedules = load.data.schedules || [];
  const groups = groupSchedulesByCode(schedules);

  return (
    <section class="report-schedule-board" data-testid="report-schedule-board">
      <div class="section-head">
        <h2>Expected reports</h2>
        <p>{PAUSE_NOTE}</p>
      </div>
      {schedules.length === 0 ? (
        <EmptyState label="No report schedules for this clinic yet." />
      ) : (
        groups.map((group) => (
          <div key={group.reportCode} class="report-chip-group" data-report-code={group.reportCode}>
            <h3>{group.reportCode}</h3>
            {group.schedules.map((row) => (
              <ScheduleChip
                key={row.id}
                row={row}
                clinic={clinic}
                expanded={openId === row.id}
                onToggleOpen={() => setOpenId((cur) => (cur === row.id ? null : row.id))}
                canToggle={canToggle}
              />
            ))}
          </div>
        ))
      )}
    </section>
  );
}
